import { memo, useCallback, useEffect, useRef } from 'react'
import { layerAtomFamily } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Dimension, ImageWidth, LayerType } from '../data/v1'
import { type SizeSetByParent, useSizeSetByParent } from '../lib/parent-sizing'
import { useDesignerActions } from '../use-designer-actions'

type HandleEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

const HANDLE_SIZE = 8

const HANDLE_DEFS: { edge: HandleEdge; cursor: string; style: React.CSSProperties }[] = [
	// Corners
	{ edge: 'nw', cursor: 'nwse-resize', style: { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 } },
	{ edge: 'ne', cursor: 'nesw-resize', style: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 } },
	{ edge: 'sw', cursor: 'nesw-resize', style: { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 } },
	{ edge: 'se', cursor: 'nwse-resize', style: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 } },
	// Edges
	{ edge: 'n', cursor: 'ns-resize', style: { top: -HANDLE_SIZE / 2, left: '50%', marginLeft: -HANDLE_SIZE / 2 } },
	{ edge: 's', cursor: 'ns-resize', style: { bottom: -HANDLE_SIZE / 2, left: '50%', marginLeft: -HANDLE_SIZE / 2 } },
	{ edge: 'w', cursor: 'ew-resize', style: { top: '50%', left: -HANDLE_SIZE / 2, marginTop: -HANDLE_SIZE / 2 } },
	{ edge: 'e', cursor: 'ew-resize', style: { top: '50%', right: -HANDLE_SIZE / 2, marginTop: -HANDLE_SIZE / 2 } },
]

function edgeAffects(edge: HandleEdge): { x: -1 | 0 | 1; y: -1 | 0 | 1 } {
	const x = edge.includes('w') ? -1 : edge.includes('e') ? 1 : 0
	const y = edge.includes('n') ? -1 : edge.includes('s') ? 1 : 0
	return { x, y }
}

function applyDelta(dim: Dimension, deltaPx: number, canvasSizePx: number): Dimension {
	if (dim.mode === '%') {
		if (canvasSizePx <= 0) { return dim }
		const deltaPercent = (deltaPx / canvasSizePx) * 100
		return { ...dim, percentValue: Math.max(1, Math.round((dim.percentValue + deltaPercent) * 100) / 100) }
	}
	return { ...dim, pxValue: Math.max(1, Math.round(dim.pxValue + deltaPx)) }
}

function toPixels(dim: Dimension, canvasSizePx: number): number {
	return dim.mode === '%' ? (dim.percentValue / 100) * canvasSizePx : dim.pxValue
}

function fromPixels(dim: Dimension, sizePx: number, canvasSizePx: number): Dimension {
	if (dim.mode === '%') {
		if (canvasSizePx <= 0) { return dim }
		return { ...dim, percentValue: Math.max(1, Math.round((sizePx / canvasSizePx) * 100 * 100) / 100) }
	}
	return { ...dim, pxValue: Math.max(1, Math.round(sizePx)) }
}

/** Mirrors the floors `fromPixels` clamps to: 1% of the canvas for a percentage, 1px otherwise. */
function minimumPixels(dim: Dimension, canvasSizePx: number): number {
	return dim.mode === '%' ? canvasSizePx / 100 : 1
}

function imageWidthToDimension(w: ImageWidth): Dimension {
	return w.mode === '%'
		? { mode: '%', pxValue: w.pxValue, percentValue: w.percentValue }
		: { mode: 'px', pxValue: w.pxValue, percentValue: w.percentValue }
}

type ShapeDrag = {
	startWidth: Dimension
	startHeight: Dimension
	canvasWidth: number
	canvasHeight: number
}

/**
 * Scales both axes together, preserving the ratio the shape had when the drag began.
 *
 * The scale is the projection of the pointer movement onto the shape's ratio diagonal — the
 * scale that puts the dragged corner as close to the cursor as a fixed ratio allows. It has
 * to be a projection rather than "whichever axis moved further drives the scale": that
 * compares absolute pixel deltas but applies a relative scale, so on a 200x100 shape the
 * driver flips when |dx| crosses |dy| and the same 2px of pointer movement jumps the width by
 * ~50px
 *
 * Both axes come off the one scale in pixels before converting back, rather than each
 * rounding its own delta — otherwise the ratio drifts over a long drag.
 */
export function constrainToAspectRatio(
	drag: ShapeDrag,
	dx: number,
	dy: number,
): { width: Dimension; height: Dimension } {
	const startWidthPx = toPixels(drag.startWidth, drag.canvasWidth)
	const startHeightPx = toPixels(drag.startHeight, drag.canvasHeight)

	if (startWidthPx <= 0 || startHeightPx <= 0) {
		return { width: drag.startWidth, height: drag.startHeight }
	}

	const scale = 1
		+ (startWidthPx * dx + startHeightPx * dy)
			/ (startWidthPx * startWidthPx + startHeightPx * startHeightPx)

	// A drag past the opposite edge would otherwise flip the shape inside out. The floor is
	// whichever axis runs out of representable size first, so both stop together and stay on
	// the ratio.
	const clampedScale = Math.max(
		scale,
		minimumPixels(drag.startWidth, drag.canvasWidth) / startWidthPx,
		minimumPixels(drag.startHeight, drag.canvasHeight) / startHeightPx,
	)

	return {
		width: fromPixels(drag.startWidth, startWidthPx * clampedScale, drag.canvasWidth),
		height: fromPixels(drag.startHeight, startHeightPx * clampedScale, drag.canvasHeight),
	}
}

function resizableAxes(layerType: LayerType, sizeSetByParent: SizeSetByParent): { x: boolean; y: boolean } {
	return {
		x: !sizeSetByParent.width,
		y: layerType !== 'image' && !sizeSetByParent.height,
	}
}

export const ResizeHandles = memo(
	function ResizeHandles(
		{ layerId, layerType, referenceEl }: { layerId: string; layerType: LayerType; referenceEl: HTMLElement },
	) {
		const { updateLayer } = useDesignerActions()
		const store = useCanvasStore()
		const sizeSetByParent = useSizeSetByParent(layerId)
		const axes = resizableAxes(layerType, sizeSetByParent)
		const dragRef = useRef<
			{
				edge: HandleEdge
				startX: number
				startY: number
				startWidth: Dimension
				startHeight: Dimension
				canvasWidth: number
				canvasHeight: number
			} | null
		>(null)

		const visibleHandles = HANDLE_DEFS.filter(({ edge }) => {
			const { x, y } = edgeAffects(edge)
			if (x !== 0 && !axes.x) { return false }
			if (y !== 0 && !axes.y) { return false }
			return true
		})

		const handlePointerDown = useCallback((e: React.PointerEvent, edge: HandleEdge) => {
			e.preventDefault()
			e.stopPropagation()
			;(e.target as HTMLElement).setPointerCapture(e.pointerId)

			const layer = store.get(layerAtomFamily(layerId))
			if (!layer) { return }

			const canvasEl = referenceEl.closest<HTMLElement>('[style*="--canvas-width"]')
			const canvasWidth = canvasEl ? parseFloat(getComputedStyle(canvasEl).getPropertyValue('--canvas-width')) : 0
			const canvasHeight = canvasEl
				? parseFloat(getComputedStyle(canvasEl).getPropertyValue('--canvas-height'))
				: 0

			let startWidth: Dimension
			let startHeight: Dimension

			if (layer.type === 'shape') {
				startWidth = layer.width
				startHeight = layer.height
			} else if (layer.type === 'image') {
				startWidth = layer.width.mode === 'auto'
					? { mode: 'px', pxValue: referenceEl.offsetWidth, percentValue: 100 }
					: imageWidthToDimension(layer.width)
				startHeight = { mode: 'px', pxValue: referenceEl.offsetHeight, percentValue: 100 }
			} else {
				return
			}

			dragRef.current = {
				edge,
				startX: e.clientX,
				startY: e.clientY,
				startWidth,
				startHeight,
				canvasWidth,
				canvasHeight,
			}

			document.body.style.cursor = HANDLE_DEFS.find((h) => h.edge === edge)!.cursor
			document.body.style.userSelect = 'none'
		}, [layerId, store, referenceEl])

		const handlePointerMove = useCallback((e: React.PointerEvent) => {
			const drag = dragRef.current
			if (!drag) { return }

			const { x: ax, y: ay } = edgeAffects(drag.edge)
			const dx = (e.clientX - drag.startX) * ax
			const dy = (e.clientY - drag.startY) * ay

			const layer = store.get(layerAtomFamily(layerId))
			if (!layer) { return }

			if (layer.type === 'shape') {
				if (e.shiftKey && ax !== 0 && ay !== 0) {
					const constrained = constrainToAspectRatio(drag, dx, dy)
					updateLayer(layerId, constrained, { transient: true })
					return
				}

				const width = ax !== 0 ? applyDelta(drag.startWidth, dx, drag.canvasWidth) : undefined
				const height = ay !== 0 ? applyDelta(drag.startHeight, dy, drag.canvasHeight) : undefined
				updateLayer(layerId, { ...(width && { width }), ...(height && { height }) }, { transient: true })
			} else if (layer.type === 'image' && ax !== 0) {
				const newDim = applyDelta(drag.startWidth, dx, drag.canvasWidth)
				const width = {
					mode: newDim.mode === '%' ? '%' as const : 'px' as const,
					pxValue: newDim.pxValue,
					percentValue: newDim.percentValue,
				}
				updateLayer(layerId, { width }, { transient: true })
			}
		}, [layerId, store, updateLayer])

		const finishDrag = useCallback(() => {
			if (!dragRef.current) { return }
			dragRef.current = null
			document.body.style.cursor = ''
			document.body.style.userSelect = ''
		}, [])

		const handlePointerUp = useCallback((e: React.PointerEvent) => {
			if (!dragRef.current) { return }
			try {
				;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
			} catch { /* noop */ }
			finishDrag()

			const layer = store.get(layerAtomFamily(layerId))
			if (!layer) { return }
			if (layer.type === 'shape') {
				updateLayer(layerId, { width: layer.width, height: layer.height })
			} else if (layer.type === 'image') {
				updateLayer(layerId, { width: layer.width })
			}
		}, [layerId, store, updateLayer, finishDrag])

		useEffect(() => finishDrag, [finishDrag])

		return (
			<>
				{visibleHandles.map(({ edge, cursor, style }) => (
					<div
						key={edge}
						style={{
							position: 'absolute',
							width: HANDLE_SIZE,
							height: HANDLE_SIZE,
							background: 'white',
							border: '2px solid #3b82f6',
							borderRadius: 2,
							cursor,
							pointerEvents: 'auto',
							zIndex: 1,
							...style,
						}}
						onPointerDown={(e) => handlePointerDown(e, edge)}
						onPointerMove={handlePointerMove}
						onPointerUp={handlePointerUp}
						onPointerCancel={handlePointerUp}
					/>
				))}
			</>
		)
	},
)
